import {
    ComponentLink,
    Link,
    makeChain,
    valueHelper,
}                              from "@Common/react/cst2";
import {Types}                 from "@Common/types";
import {userLevelCanSeePrices} from "@Common/logic/check-show-pricing";
import {BubbleNoti}            from "@Common/ui-components/bubble-noti/bubble-noti";
import {cascadeSum}            from "@Common/utils/cascade";
import {parsePhpDate}          from "@Common/utils/date/parse-php-date";
import {isEmptyContainer}      from "@Common/utils/objects";
import {consumeContext}        from "@Xpresso/loaders/consume-context";
import {PROGRAM_TYPE}          from "@Xpresso/routes/common/logic/program";

import {CancelAction}                                   from "../common/cancel-action";
import {DeliveryStatus}                                 from "../common/delivery-status/delivery-status";
import {OrderDetailsSidebar}                            from "../common/order-details/order-details-sidebar";
import {ThumbnailPreviewModal}                          from "../common/thumbnail-preview-modal/thumbnail-preview-modal";
import {cartShouldShowPrice, cartWasPaidWithCreditCard} from "./cart-attributes";
import {DownloadPhotosModal}                            from "./order-history-expansion/item-card/download-photos-modal/download-photos-modal";
import {ReorderAction}                                  from "./reorder-action/reorder-action";

const DELIVERY_TYPE_CANADA          = "Mailed (CA)";
const DELIVERY_TYPES_TRACEABLE      = ["Mailed", "Shipped"];
const DURATION_NOTIFICATION_MS      = 5000
const ONEMIN_MS                     = 1000 * 60;
const ONEDAY_MS                     = ONEMIN_MS * 60 * 24;
const TRANS_ORDER_STATUS_CANCELLED  = "cancelled";
export const messageReorderDisallowed = "Scheduled Campaigns templates cannot be reordered outside of the Scheduled Campaigns program."

export type ItemAttribute = {label: string} & ({
    key      : "downloadPhotos" | "downloadProof" | "downloadZip" | "downloadImagePDF" | "download" | "preview" | "reorder" | "reorderDisallowed" | "showDetails" | "xpressLinks" | "placedBy" | "approvedBy",
    value   ?: string,
    run     ?: () => Promise<void>,
} | {
    key         : "cancel",
    allowed     : boolean,
    expiration  : Date,
    run         : () => Promise<void>,
} | {
    key      : "trackDeliveryStatus",
    message ?: string,
    run     ?: () => Promise<void>,
} | {
    key     : "name" | "programName",
    value   : string,
} | {
    key     : "thumbnail",
    value   : string,
} | {
    key     : "price",
    value   : number,
} | {
    key     : "kit",
    length  : number,
});

export type ItemAttributeKey = ItemAttribute['key'];

export type ItemAttributeDict = {
    [Key in ItemAttributeKey]?: ItemAttribute & {key: Key};
};

type Cart = Types.Apis.Payloads.Next.Item.GetHistory.Cart;
type Item = Types.Apis.Payloads.Next.Item.GetHistory.Item;

/**
 * Centralizes logic of items in order history
 *
 * "ItemAttributes" are available details and actions of an item.
 */
export const ItemAttributesService = ({cart, renderChildren, onItemCancelled: emitItemCancelled, onItemReordered: emitItemReordered}:{
    cart            ?: Cart,
    renderChildren   : Link<JSX.Element, {
        mapItemAsAttributes : (item:Item) => ItemAttributeDict,
    }>,
    onItemCancelled ?: (item:Item) => void,
    onItemReordered ?: (item:Item) => void,
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("company"))
    .then(...consumeContext("systemConstants"))
    .then(...consumeContext("userLevels"))
    .then("cancelModal", (({}, next:ComponentLink<typeof CancelAction>) => (
        CancelAction({next, onCancelled: emitItemCancelled})
    )))
    .then("deliveryStatus", (({}, next:ComponentLink<typeof DeliveryStatus>) => (
        DeliveryStatus({next})
    )))
    .then("downloadPhotosModal", (({}, next:ComponentLink<typeof DownloadPhotosModal>) => (
        DownloadPhotosModal({
            next,
            title: "Available Photos",
            urlParam: "photos",
        })
    )))
    .then("enlargedView", ({}, next:ComponentLink<typeof ThumbnailPreviewModal, "renderSibling">) => (
        ThumbnailPreviewModal({renderSibling: next})
    ))
    .then("orderDetailsSidebar", (({}, next:ComponentLink<typeof OrderDetailsSidebar>) => (
        OrderDetailsSidebar({next})
    )))
    .then("reorderModal", (({}, next:ComponentLink<typeof ReorderAction>) => (
        ReorderAction({next, onReordered: emitItemReordered})
    )))
    .then("reorderDisallowed", (({}, next: ComponentLink<typeof BubbleNoti>) => (
        BubbleNoti({next})
    )))
    .then("mapItemAsPayments", valueHelper(({company, userLevels: {userLevel}}) => (
        (item:Item) => {
            if (!cart || (!userLevelCanSeePrices({company, userLevel}) && !cartShouldShowPrice(cart))) {
                return [];
            }

            const payments = [
                ...cart.appliedCredits?.storeCredits?.filter(({itemId}) => itemId == item.itemId)?.map((storeCredit) => ({
                    type: "Store Credit",
                    value: storeCredit.amount,
                })) ?? [],
                ...cart.appliedCredits?.adBudgets?.filter(({itemId}) => itemId == item.itemId)?.map((adBudget) => ({
                    type: "Ad Budget",
                    value: +adBudget.amount,
                    comments: adBudget.comments,
                })) ?? [],
                ...cart.appliedCredits?.vouchers?.filter(({itemId}) => itemId == item.itemId)?.map((voucher) => ({
                    type: "Voucher",
                    label: voucher.name,
                    value: voucher.amount,
                })) ?? [],
                ...cart.appliedCredits?.copays?.filter(({itemId}) => itemId == item.itemId)?.map((copay) => ({
                    type: "Copay",
                    value: copay.amount,
                })) ?? [],
                ...cart.appliedPayments?.billing?.filter(({itemId}) => itemId == item.itemId)?.map((billing) => ({
                    type: "Billing",
                    label: billing.name,
                    value: billing.amount,
                    comments: billing.comments,
                    meta1Value: billing.meta1,
                    meta1Label: billing.meta1Label,
                    meta2Value: billing.meta2,
                    meta2Label: billing.meta2Label,
                })) ?? [],
            ];

            if (cartWasPaidWithCreditCard(cart)) {
                payments.push({
                    type: "Credit Card",
                    value: item.price - cascadeSum(payments, "[*].value"),
                });
            }

            return payments;
        }
    )))
    .then(({
        cancelModal,
        company,
        deliveryStatus,
        downloadPhotosModal,
        enlargedView,
        mapItemAsPayments,
        orderDetailsSidebar,
        reorderModal,
        reorderDisallowed,
        userLevels: {userLevel},
        systemConstants,
    }) => renderChildren({
        mapItemAsAttributes: (item) => {
            const attributes:ItemAttribute[] = [
                {
                    key     : "trackDeliveryStatus",
                    label   : "Status",
                    message : item.deliveryStatus === DELIVERY_TYPE_CANADA ? "Canada Post is not eligible for tracing" : null,
                    run     : DELIVERY_TYPES_TRACEABLE.includes(item.deliveryStatus)
                        ? async () => deliveryStatus.show({item})
                        : null,
                },
                {
                    key     : "name",
                    label   : "Order Name",
                    value   : item.itemName || item.metaData.templateName || "",
                },
                {
                    key     : "programName",
                    label   : "Program",
                    value   : item.programData?.name ?? item.programName ?? item.programType?.toUpperCase() ?? "Main",
                },
                {
                    key     : "showDetails",
                    label   : "Order Details",
                    run     : async () => orderDetailsSidebar.show({orderDetails: {item, payments: mapItemAsPayments(item)}}),
                },
            ];

            const itemIsCancellable = itemSupportsCancellation(item);
            if (itemIsCancellable || item.selections.distributionType === "download") {
                attributes.push({
                    key         : "cancel",
                    label       : "Cancel Order",
                    allowed     : itemIsCancellable,
                    expiration  : mapItemAsCancellationExpiration(item),
                    run         : itemIsCancellable ? async () => cancelModal.show({item}) : null,
                });
            }

            if (!item?.proofUrls?.combinedPdf?.length && item?.photoCount > 0) {
                const label = item?.photoCount > 1 ? "Download Photos" : "Download Photo"
                attributes.push({
                    key     : "downloadPhotos",
                    label   : label,
                    run     : async () => downloadPhotosModal.show(item)
                });
            }

            if (userLevelCanSeePrices({company, userLevel}) || cartShouldShowPrice(cart)) {
                attributes.push({
                    key     : "price",
                    label   : "Price",
                    value   : item.price,
                });
            }

            if (itemSupportsDownload(item)) {
                let labelZip = "Download Image"
                if(item?.photoCount > 1 || item?.itemThumbnails?.length > 1) {
                    attributes.push({
                        key     : "downloadImagePDF",
                        label   : "Download PDF",
                        run : async () => {window.open(`/next/web_services/get_production_pdf.php?id_order=${item.itemId}`, "_blank")}
                    })
                    labelZip = "Download Zip"
                }

                attributes.push({
                    key     : "download",
                    label   : labelZip,
                    run : async () => {item.proofUrls?.zip ? window.open(`${item.proofUrls.zip[0].path}`, "_blank") : window.open(`/next/web_services/get_production_download.php?id_order=${item.itemId}&file_type=${item.selections?.fileType}`, "_blank")}
                })
            }

            if (itemSupportsProofDownload(item) && company?.prefs.allowPdfPurchase && item.proofUrls?.pdf?.length > 1) {
                attributes.push({
                    key   : "downloadZip",
                    label : "Download Zip",
                    run   : async () => {window.open(`/next/web_services/get_production_download.php?id_order=${item.itemId}&file_type=pdf`, "_blank")}
                });
            }

            if (itemSupportsProofDownload(item) && company?.prefs.allowPdfPurchase) {
                const url = item?.proofUrls?.combinedPdf?.length > 0
                    ? item?.proofUrls?.combinedPdf[0].path
                    : `/next/web_services/get_production_pdf.php?id_order=${item.itemId}`
                attributes.push({
                    key   : "downloadProof",
                    label : "Download PDF",
                    run   : async () => {window.open(url, "_blank")}
                });
            }

            if (
                !itemIsKit(item) &&
                new Date(item.orderDate.date).getTime() + ONEDAY_MS * 540 > new Date().getTime() &&
                item?.programType !== PROGRAM_TYPE.VISIBILITY_PLUS
            ) {
                item?.programType === PROGRAM_TYPE.SCHEDULED_CAMPAIGNS
                    ? attributes.push({
                        key: "reorderDisallowed",
                        label: "Reorder",
                        run : async () => reorderDisallowed.show({message: messageReorderDisallowed, color: "black", duration: DURATION_NOTIFICATION_MS})
                      })
                    : attributes.push({
                        key: "reorder",
                        label: "Reorder",
                        run: async () => reorderModal.show({item})
                      })
            }

            if (!itemIsKit(item) && (item.thumbnail || item.proofUrls?.jpg)) {
                attributes.push({
                    key     : "preview",
                    label   : "Preview",
                    run     : async () => enlargedView.show(item?.proofUrls?.jpg || [{url: item.thumbnail}]),
                });
            }

            if (item.thumbnail) {
                attributes.push({
                    key     : "thumbnail",
                    label   : "Thumbnail",
                    value   : item.thumbnail,
                });
            }

            if (itemSupportsXpressLinks(item) && company.prefs.enableXpresslinks) {
                attributes.push({
                    key     : "xpressLinks",
                    label   : "Xpresslinks",
                    run     : async () => {
                        const data = {
                            idCompany: company.idCompany,
                            idOrder: item.itemId.toString(),
                        }
                        const jsonString = JSON.stringify(data)
                        const base64String = btoa(jsonString)
                        window.open(systemConstants?.value?.MN_XPRESSLINKS_URL + encodeURIComponent(base64String))
                    },
                });
            }

            if (itemIsKit(item) ) {
                attributes.push({
                    key     : "kit",
                    label   : "Kit",
                    length  : Object.values(item.components).filter(component => component?.selections?.quantity > 0).length
                });
            }

            if (item?.placedBy) {
                attributes.push({
                    key     : "placedBy",
                    label   : "Placed by",
                    value   : item?.placedBy,
                });
            }

            if (item?.approvedBy) {
                attributes.push({
                    key     : "approvedBy",
                    label   : "Approved by",
                    value   : item?.approvedBy,
                });
            }

            return Object.fromEntries(attributes.map(attribute => [attribute.key, attribute]));
        },
    }))
    .run()
);

const itemSupportsCancellation = (item:Item) => (
    item.transOrderStatus !== "cancelled" && item.selections.distributionType.toLowerCase() !== "download"
    && mapItemAsCancellationExpiration(item).getTime() > new Date().getTime()
);

const itemSupportsProofDownload = (item:Item) => (
    item.transOrderStatus !== TRANS_ORDER_STATUS_CANCELLED
    && (
        item.selections.options.find(v => v.dev_key === "pdf_download")?.value
    )
);

const itemSupportsXpressLinks = (item:Item) => (
    item?.proofUrls?.jpg
    && item.transOrderStatus !== "cancelled"
    && (item.param?.allowXpresslinks ?? true)
    && ((parsePhpDate(item.orderDate).getTime() + (ONEDAY_MS * 90)) > new Date().getTime())
);

const mapItemAsCancellationExpiration = (item:Item) => (
    item.scheduledDate
        ? parsePhpDate(item.scheduledDate)
        : new Date(parsePhpDate(item.orderDate).getTime() + (ONEMIN_MS * 10))
);

const itemSupportsDownload = (item:Item) => (
    item.transOrderStatus !== TRANS_ORDER_STATUS_CANCELLED
    && item.selections.distributionType.toLowerCase() === "download"
);

const itemIsKit = (item:Item) => (
    !isEmptyContainer(item.components)
);
